// src/pages/Exchangers.js — Admin exchanger list (mirrors Users.js patterns)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import toast from "react-hot-toast";
import { fetchExchangerList } from "../services/exchangerApi";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaSyncAlt,
    FaSearch,
    FaChevronLeft,
    FaChevronRight,
    FaStore,
    FaCheckCircle,
    FaTimesCircle,
    FaCopy,
    FaInfoCircle,
    FaEye,
    FaEyeSlash,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaLock,
    FaUserCircle,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 20;

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v) {
    const num = Number(v || 0);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) {
        /* ignore */
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled =
        disabled || value === null || value === undefined || value === "" || value === "—";

    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };

    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-2 py-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
            style={{ fontSize: "0.75rem" }}
        >
            <FaCopy />
        </button>
    );
}

function LoadingRow({ cols = 8 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i} className="px-4 py-3">
                    <div className="placeholder-glow">
                        <span className="placeholder col-10" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page <span className="fw-semibold">{pageNo}</span> of{" "}
                <span className="fw-semibold">{totalPages}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft /> Prev
                </button>
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    Next <FaChevronRight />
                </button>
                <div className="d-flex align-items-center gap-2 ms-2">
                    <span className="text-muted small">Go to</span>
                    <select
                        className="form-select form-select-sm"
                        value={pageNo}
                        onChange={(e) => onJump(Number(e.target.value))}
                        disabled={loading || totalPages <= 1}
                        style={{ width: "70px", padding: "0.25rem 0.5rem" }}
                    >
                        {pages.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

function serialNo(pageNo, index) {
    return (Math.max(1, pageNo) - 1) * PAGE_SIZE + index + 1;
}

function InfoModal({ open, user, onClose }) {
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (open) setShowPassword(false);
    }, [open]);

    if (!user) return null;

    const id = user?.id;
    const name = safeText(user?.name);
    const cc = safeText(user?.country_code);
    const mobile = safeText(user?.mobile);
    const email = safeText(user?.email);
    const pwd = user?.password ?? "";
    const mobileDisplay = mobile === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobile}`.trim();

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            inset: 0,
                            zIndex: 1040,
                        }}
                    />
                    <div
                        className="modal show d-block"
                        tabIndex={-1}
                        style={{ position: "fixed", inset: 0, zIndex: 1050, overflowY: "auto" }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 12 }}>
                                <div className="modal-header border-bottom p-4 bg-light">
                                    <div>
                                        <h5 className="modal-title fw-bold mb-1">Exchanger Details</h5>
                                        <div className="text-muted small">{name}</div>
                                    </div>
                                    <button type="button" className="btn-close" onClick={onClose} />
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="text-muted small mb-1">
                                                <FaUser className="me-2 text-primary" />
                                                User ID
                                            </div>
                                            <div className="fw-semibold d-flex align-items-center gap-2">
                                                {safeText(id)}
                                                <CopyBtn value={id} label="ID copied" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="text-muted small mb-1">
                                                <FaUserCircle className="me-2 text-primary" />
                                                Name
                                            </div>
                                            <div className="fw-semibold">{name}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="text-muted small mb-1">
                                                <FaPhone className="me-2 text-primary" />
                                                Mobile
                                            </div>
                                            <div className="fw-semibold d-flex align-items-center gap-2">
                                                {mobileDisplay}
                                                <CopyBtn value={mobile === "—" ? "" : mobile} label="Mobile copied" />
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="text-muted small mb-1">
                                                <FaEnvelope className="me-2 text-primary" />
                                                Email
                                            </div>
                                            <div className="fw-semibold d-flex align-items-center gap-2">
                                                {email}
                                                <CopyBtn value={email === "—" ? "" : email} label="Email copied" />
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="text-muted small mb-1">
                                                <FaLock className="me-2 text-primary" />
                                                Password
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="mono fw-semibold">
                                                    {showPassword && pwd ? pwd : "••••••••"}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setShowPassword((s) => !s)}
                                                    disabled={!pwd}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                {showPassword && pwd ? (
                                                    <CopyBtn value={pwd} label="Password copied" />
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="text-muted small mb-1">Status</div>
                                            {user?.status ? (
                                                <span className="badge bg-success px-3 py-2">
                                                    <FaCheckCircle className="me-2" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="badge bg-danger px-3 py-2">
                                                    <FaTimesCircle className="me-2" />
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-top p-4 bg-light">
                                    <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default function Exchangers() {
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total_user: 0, page_no: 1 });
    const [selectedUser, setSelectedUser] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const { logout } = useAuth();

    const totalPages = useMemo(() => {
        const total = Number(info?.total_user || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_user]);

    const fetchExchangers = useCallback(
        async ({ page, query }) => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token") || "";
                if (!token) {
                    toast.error("Token not found. Please login again.");
                    logout();
                    return;
                }

                const data = await fetchExchangerList({
                    token,
                    page_no: page,
                    query: query || "",
                });

                if (data?.error) {
                    if (data?.error === "session expired") {
                        logout();
                        return;
                    }
                    toast.error(typeof data?.error === "string" ? data.error : "Failed to load exchangers");
                    setList([]);
                    setInfo({ total_user: 0, page_no: page });
                    return;
                }

                const raw = Array.isArray(data?.user_list) ? data.user_list : [];
                setList(raw);
                const apiInfo = data?.info || {};
                setInfo({
                    total_user: Number(apiInfo?.total_user || 0),
                    page_no: Number(apiInfo?.page_no || page),
                });
            } catch (err) {
                console.log(err);
                toast.error("Failed to load exchangers");
                setList([]);
                setInfo({ total_user: 0, page_no: page });
            } finally {
                setLoading(false);
            }
        },
        [logout]
    );

    useEffect(() => {
        fetchExchangers({ page: 1, query: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchExchangers({ page: pageNo, query: appliedQuery });
    }, [pageNo, appliedQuery, fetchExchangers]);

    useEffect(() => {
        const t = setTimeout(() => {
            const q = inputText.trim();
            if (q === appliedQuery) return;
            setPageNo(1);
            setAppliedQuery(q);
        }, 300);
        return () => clearTimeout(t);
    }, [inputText, appliedQuery]);

    const onSearch = () => {
        const q = inputText.trim();
        setPageNo(1);
        setAppliedQuery(q);
        setInfoOpen(false);
        setSelectedUser(null);
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setPageNo(1);
        setInfoOpen(false);
        setSelectedUser(null);
        await fetchExchangers({ page: 1, query: "" });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const openInfo = (user) => {
        setSelectedUser(user);
        setInfoOpen(true);
    };
    const closeInfo = () => {
        setInfoOpen(false);
        setSelectedUser(null);
    };

    return (
        <PageTransition>
            <style>{`
                .users-table {
                    width: 100%;
                    margin-bottom: 0;
                    border-collapse: separate;
                    border-spacing: 0;
                }
                .users-table th {
                    background-color: #f8f9fa;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.5px;
                    padding: 12px 8px;
                    border-bottom: 2px solid #dee2e6;
                    white-space: nowrap;
                }
                .users-table td {
                    padding: 12px 8px;
                    vertical-align: middle;
                    border-bottom: 1px solid #e9ecef;
                    font-size: 0.875rem;
                }
                .users-table tbody tr:hover {
                    background-color: rgba(0, 0, 0, 0.02);
                }
                .cell-ellipsis {
                    max-width: 160px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .mono {
                    font-family: SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace;
                    font-size: 0.85rem;
                }
                .badge-compact {
                    padding: 4px 10px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                .table-card {
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                }
            `}</style>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                <div>
                    <h2 className="fw-bold mb-1">Exchanger List</h2>
                    <div className="text-muted">
                        Total Exchangers:{" "}
                        <span className="fw-semibold text-primary">{nfmt(info.total_user)}</span>
                        {appliedQuery ? (
                            <>
                                {" "}
                                • Search: <span className="fw-semibold text-info">{appliedQuery}</span>
                            </>
                        ) : null}
                    </div>
                </div>
                <button
                    className="btn btn-outline-primary btn-sm px-3 py-2 d-inline-flex align-items-center gap-2"
                    onClick={onRefreshReset}
                    disabled={loading}
                    type="button"
                >
                    <FaSyncAlt className={loading ? "tb-spin" : ""} />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-12 col-lg-7">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="input-group flex-grow-1" style={{ minWidth: 220 }}>
                                    <span className="input-group-text bg-light border-end-0">
                                        <FaSearch className="text-muted" />
                                    </span>
                                    <input
                                        className="form-control border-start-0"
                                        placeholder="Search name, mobile, email, ID…"
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") onSearch();
                                        }}
                                    />
                                </div>
                                <button
                                    className="btn btn-primary px-3 d-inline-flex align-items-center gap-2"
                                    onClick={onSearch}
                                    disabled={loading}
                                    type="button"
                                >
                                    <FaSearch /> Search
                                </button>
                            </div>
                        </div>
                        <div className="col-12 col-lg-5">
                            <PaginationBar
                                loading={loading}
                                pageNo={pageNo}
                                totalPages={totalPages}
                                onPrev={onPrev}
                                onNext={onNext}
                                onJump={onJump}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0 table-card">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0 users-table">
                            <thead>
                                <tr>
                                    <th className="text-center" style={{ width: "52px" }}>
                                        #
                                    </th>
                                    <th style={{ minWidth: "72px" }}>ID</th>
                                    <th style={{ minWidth: "130px" }}>Name</th>
                                    <th style={{ minWidth: "150px" }}>Mobile</th>
                                    <th style={{ minWidth: "160px" }}>Email</th>
                                    <th style={{ minWidth: "90px" }}>Status</th>
                                    <th className="text-center" style={{ minWidth: "72px" }}>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <>
                                        <LoadingRow cols={7} />
                                        <LoadingRow cols={7} />
                                        <LoadingRow cols={7} />
                                        <LoadingRow cols={7} />
                                        <LoadingRow cols={7} />
                                    </>
                                ) : list.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center gap-3">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: 64,
                                                        height: 64,
                                                        background: "rgba(13,110,253,0.1)",
                                                        color: "#0d6efd",
                                                    }}
                                                >
                                                    <FaStore size={28} />
                                                </div>
                                                <div className="fw-semibold fs-5">No exchangers found</div>
                                                <div className="text-muted">
                                                    {appliedQuery
                                                        ? "Try a different search term"
                                                        : "No exchangers in the system"}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    list.map((u, idx) => {
                                        const id = u?.id;
                                        const rowKey = String(id ?? idx);
                                        const name = safeText(u?.name);
                                        const cc = safeText(u?.country_code);
                                        const mobile = safeText(u?.mobile);
                                        const email = safeText(u?.email);
                                        const mobileDisplay =
                                            mobile === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobile}`.trim();

                                        return (
                                            <tr key={rowKey}>
                                                <td className="text-muted small text-center fw-semibold">
                                                    {serialNo(pageNo, idx)}
                                                </td>
                                                <td className="fw-semibold">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="mono text-primary">{safeText(id)}</span>
                                                        <CopyBtn value={id} label="ID copied" />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold cell-ellipsis" title={name}>
                                                        {name}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span
                                                            className="fw-semibold cell-ellipsis"
                                                            title={mobileDisplay}
                                                        >
                                                            {mobileDisplay}
                                                        </span>
                                                        <CopyBtn
                                                            value={mobile === "—" ? "" : mobile}
                                                            label="Mobile copied"
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span
                                                            className="cell-ellipsis"
                                                            title={email}
                                                            style={{ maxWidth: 140 }}
                                                        >
                                                            {email}
                                                        </span>
                                                        <CopyBtn
                                                            value={email === "—" ? "" : email}
                                                            label="Email copied"
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    {u?.status ? (
                                                        <span className="badge bg-success badge-compact d-inline-flex align-items-center gap-1">
                                                            <FaCheckCircle /> Active
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-danger badge-compact d-inline-flex align-items-center gap-1">
                                                            <FaTimesCircle /> Inactive
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-info px-3 py-1"
                                                        onClick={() => openInfo(u)}
                                                        title="View details"
                                                    >
                                                        <FaInfoCircle />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-top p-3 bg-light">
                        <PaginationBar
                            loading={loading}
                            pageNo={pageNo}
                            totalPages={totalPages}
                            onPrev={onPrev}
                            onNext={onNext}
                            onJump={onJump}
                        />
                    </div>
                </div>
            </div>

            <InfoModal open={infoOpen} user={selectedUser} onClose={closeInfo} />
        </PageTransition>
    );
}
